"use client";

import { GoogleTagManager } from "@next/third-parties/google";
import React, { createContext, useContext, useMemo, useState } from "react";

import {
  getStoredAnalyticsConsent,
  setStoredAnalyticsConsent,
  type TAnalyticsConsent,
} from "@/lib/analytics/consent";

interface IAnalyticsContextValue {
  consent: TAnalyticsConsent | null;
  setConsent: (consent: TAnalyticsConsent) => void;
}

const AnalyticsContext = createContext<IAnalyticsContextValue | null>(null);

export function useAnalyticsConsent(): IAnalyticsContextValue {
  const value = useContext(AnalyticsContext);
  if (!value) {
    throw new Error("useAnalyticsConsent must be used within AnalyticsProvider");
  }
  return value;
}

export function AnalyticsProvider({
  children,
  initialConsent = null,
}: {
  children: React.ReactNode;
  initialConsent?: TAnalyticsConsent | null;
}): React.ReactElement {
  /**
   * Hydration safety:
   * - On the server, there is no localStorage/cookie access.
   * - If we read consent during the first client render, we can diverge from the server HTML
   *   (e.g. injecting GTM immediately) and trigger hydration errors.
   *
   * So we always start as `null` and hydrate the stored value after mount.
   */
  const [consent, setConsentState] = useState<TAnalyticsConsent | null>(
    initialConsent
  );

  React.useEffect(() => {
    // Reconcile with client-side storage (localStorage > cookie) after mount.
    setConsentState(getStoredAnalyticsConsent());
  }, []);

  const setConsent = (next: TAnalyticsConsent) => {
    setStoredAnalyticsConsent(next);
    setConsentState(next);
  };

  const value = useMemo<IAnalyticsContextValue>(
    () => ({ consent, setConsent }),
    [consent]
  );

  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
  const shouldLoadGtm =
    consent === "granted" && typeof gtmId === "string" && gtmId.length > 0;

  return (
    <AnalyticsContext.Provider value={value}>
      {shouldLoadGtm ? <GoogleTagManager gtmId={gtmId} /> : null}
      {children}
    </AnalyticsContext.Provider>
  );
}

